import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

/**
 * TopRotatingBanner Component
 * 
 * Eye-catching animated banner with 3 rotating cards at the top of the page.
 * Features:
 * - Fixed at very top (responsive for all screen sizes)
 * - 3 animated cards rotating continuously
 * - Smooth transitions and animations
 * - Gradient backgrounds and modern design
 * - Responsive layout for mobile, tablet, and desktop
 */
const TopRotatingBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // 3 eye-catching cards with image-like visual design
  const cards = [
    {
      title: 'Spend Up, Growth Stalled?',
      mobileTitle: 'Growth Stalled Now?',
      subtitle: 'Find Revenue Leaks Fast',
      mobileSubtitle: 'Find revenue leaks fast',
      description: 'Get a free Growth Leak Audit to find product, funnel, and automation gaps fast.',
      icon: 'Rocket',
      gradient: 'from-[#00d690] via-[#00b876] to-[#009966]',
      cardBg: 'from-[#0a1929] via-[#112240] to-[#1a2f4a]', // Dark blue card background
      cardBorder: 'border-[#00d690]/40',
      accentColor: '#00d690',
      cta: 'Free Audit',
      ctaMobile: 'Audit',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      title: 'Shipping Features But Not Results?',
      mobileTitle: 'Shipping Features, No Results?',
      subtitle: 'Turn Roadmap Into ROI',
      mobileSubtitle: 'Turn your roadmap into ROI',
      description: 'Get a conversion-first plan that ties each sprint to leads, retention, and revenue.',
      icon: 'TrendingUp',
      gradient: 'from-[#6366f1] via-[#4f46e5] to-[#4338ca]',
      cardBg: 'from-[#1e1b4b] via-[#312e81] to-[#4338ca]', // Deep purple card background
      cardBorder: 'border-[#6366f1]/40',
      accentColor: '#6366f1',
      cta: 'View Services',
      ctaMobile: 'Services',
      action: () => {
        window.location.href = '/services#services';
      }
    },
    {
      title: 'Still Losing Hours to Manual Ops?',
      mobileTitle: 'Losing Hours to Manual Ops?',
      subtitle: 'Automate Before Costs Rise',
      mobileSubtitle: 'Automate before your costs rise',
      description: 'Get a free Automation Blueprint with high-ROI workflows for n8n, Zapier, Make, and AI copilots.',
      icon: 'Zap',
      gradient: 'from-[#06b6d4] via-[#0891b2] to-[#0e7490]', // Cyan/teal gradient
      cardBg: 'from-[#083344] via-[#164e63] to-[#155e75]', // Dark cyan/teal card background
      cardBorder: 'border-[#06b6d4]/40',
      accentColor: '#06b6d4',
      cta: 'Free Blueprint',
      ctaMobile: 'Blueprint',
      action: () => {
        window.location.href = '/services/workflow-automation#what-we-offer';
      }
    }
  ];

  // Auto-rotation logic - continuous rotation
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
    }, 14000); // Rotate every 14 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [cards.length]);

  // Animation variants for card transitions
  const cardVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.8
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.8
    })
  };

  const transition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    duration: 0.6
  };

  const currentCard = cards[currentIndex];
  const direction = 1;

  return (
    <section
      className="w-full fixed top-0 left-0 right-0 z-[1002] overflow-hidden"
      aria-label="Rotating promotional cards"
      role="region"
      style={{ 
        zIndex: 1002,
        top: 0,
        left: 0,
        right: 0
      }}
    >
      <div className="relative h-[60px] sm:h-[65px] md:h-[70px] lg:h-[80px] overflow-hidden">
        {/* Banner strip background - gray */}
        <div className="absolute inset-0 bg-gray-200 transition-all duration-700" />
        
        {/* Banner strip decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Subtle pattern for strip - removed for transparent background */}
          {/* Subtle shine on strip - removed for transparent background */}
        </div>

        {/* Card content - image-like composition */}
        <div className="relative h-full flex items-center justify-center z-10">
          <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className={`relative flex items-center justify-between gap-2 sm:gap-3 md:gap-4 lg:gap-4 xl:gap-6 max-w-7xl mx-auto w-full bg-gradient-to-r ${currentCard.cardBg} rounded-lg sm:rounded-xl px-3 sm:px-4 md:px-6 lg:px-6 xl:px-8 py-2 sm:py-2.5 md:py-3 lg:py-4 border ${currentCard.cardBorder} shadow-2xl backdrop-blur-sm overflow-hidden`}
                style={{
                  boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 40px ${currentCard.accentColor}20`
                }}
              >
                {/* Card-specific decorative elements */}
                <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                  {/* Animated gradient blobs for card */}
                  <div 
                    className="absolute -right-10 -top-5 w-32 h-32 rounded-full opacity-20 blur-2xl transition-all duration-700"
                    style={{ 
                      background: `radial-gradient(circle, ${currentCard.accentColor}60, transparent)`,
                      transform: `scale(${1 + Math.sin(currentIndex * 2) * 0.2})`
                    }}
                  />
                  <div 
                    className="absolute -left-5 -bottom-5 w-24 h-24 rounded-full opacity-15 blur-xl transition-all duration-700"
                    style={{ 
                      background: `radial-gradient(circle, ${currentCard.accentColor}80, transparent)`,
                      transform: `scale(${1 + Math.cos(currentIndex * 2) * 0.15})`
                    }}
                  />
                  
                  {/* Card shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />
                  
                  {/* Subtle grid on card */}
                  <div 
                    className="absolute inset-0 opacity-3"
                    style={{
                      backgroundImage: `linear-gradient(${currentCard.accentColor}30 1px, transparent 1px), linear-gradient(90deg, ${currentCard.accentColor}30 1px, transparent 1px)`,
                      backgroundSize: '15px 15px'
                    }}
                  />
                </div>
                {/* Left side - Icon and Title with image-like styling */}
                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-3 xl:gap-4 flex-1 relative z-20 min-w-0">
                  {/* Decorative accent shape - hidden on mobile */}
                  <div 
                    className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 sm:h-10 md:h-12 bg-white/30 rounded-full hidden sm:block"
                    style={{ 
                      boxShadow: `0 0 10px ${currentCard.accentColor}80`
                    }}
                  />
                  
                  <motion.div
                    className="relative z-10 flex-shrink-0"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  >
                    {/* Icon with image-like depth */}
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gradient-to-br ${currentCard.gradient} rounded-lg sm:rounded-xl flex items-center justify-center shadow-2xl relative overflow-hidden border-2 border-white/20`}>
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                      {/* Inner glow */}
                      <div className="absolute inset-1 bg-white/10 rounded-md sm:rounded-lg" />
                      <Icon
                        name={currentCard.icon}
                        size={16}
                        className="sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white relative z-10 drop-shadow-lg"
                      />
                    </div>
                    {/* Outer glow effect - image-like depth */}
                    <div 
                      className={`absolute inset-0 bg-gradient-to-br ${currentCard.gradient} rounded-lg sm:rounded-xl blur-xl opacity-60 -z-10 animate-pulse`}
                      style={{
                        filter: `blur(20px) drop-shadow(0 0 15px ${currentCard.accentColor}60)`
                      }}
                    />
                  </motion.div>
                  
                  <div className="flex flex-col relative z-10 min-w-0 max-w-full">
                    <motion.h2
                      className="text-[11px] sm:text-sm md:text-base lg:text-xl xl:text-2xl font-bold text-white leading-tight drop-shadow-lg break-words pr-1"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      style={{
                        textShadow: `0 2px 10px rgba(0,0,0,0.3), 0 0 20px ${currentCard.accentColor}40`
                      }}
                    >
                      <span className="sm:hidden">{currentCard.mobileTitle || currentCard.title}</span>
                      <span className="hidden sm:inline">{currentCard.title}</span>
                    </motion.h2>
                    <motion.p
                      className="text-[9px] sm:text-xs md:text-xs lg:text-sm text-white/90 font-medium leading-tight break-words pr-1 block"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      style={{
                        textShadow: '0 1px 5px rgba(0,0,0,0.2)'
                      }}
                    >
                      <span className="sm:hidden">{currentCard.mobileSubtitle || currentCard.subtitle}</span>
                      <span className="hidden sm:inline">{currentCard.subtitle}</span>
                    </motion.p>
                  </div>
                </div>

                {/* Center - Description with image-like text styling */}
                <motion.div
                  className="flex-1 text-center hidden md:block relative z-20 min-w-0 px-1 sm:px-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <p 
                    className="text-[10px] sm:text-xs md:text-xs lg:text-sm text-white/90 leading-tight mx-auto font-medium"
                    style={{
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                  >
                    {currentCard.description}
                  </p>
                </motion.div>

                {/* Right side - CTA Button with image-like styling */}
                <motion.div
                  className="flex-shrink-0 relative z-20 ml-1"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, type: 'spring' }}
                >
                  <button
                    onClick={currentCard.action}
                    className={`bg-white/95 backdrop-blur-sm text-gray-900 px-2 sm:px-3 md:px-4 lg:px-5 py-1 sm:py-1.5 md:py-1.5 lg:py-2 text-[10px] sm:text-xs md:text-xs lg:text-sm font-bold rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 relative overflow-hidden group border border-white/50 btn-animate-float`}
                    style={{
                      boxShadow: `0 4px 15px rgba(0,0,0,0.2), 0 0 20px ${currentCard.accentColor}30`
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${currentCard.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    <span className="relative z-10 flex items-center gap-1 sm:gap-1.5 transition-colors duration-300 group-hover:text-white font-semibold">
                      <span className="hidden sm:inline">{currentCard.cta}</span>
                      <span className="sm:hidden">{currentCard.ctaMobile || currentCard.cta}</span>
                      <Icon name="ArrowRight" size={10} className="sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TopRotatingBanner;

